import React, { useState, useEffect } from 'react';
import { useData } from '../context/DataContext';
import { useWorkflow } from '../context/WorkflowContext';
import { getUserRole } from '../api';
import { getStudentDisplayName } from '../utils/studentName';
import '../styles/session-filters.css';

/**
 * SessionFilters Component
 *
 * Filter controls for the Sessions page:
 * - Student dropdown (preselected from workflow context)
 * - Subject/topic filter
 * - Date range filter
 *
 * Uses DataContext for cached student list (no duplicate fetching)
 */

const SessionFilters = ({ onFiltersChange, initialStudentId = null, hideStudentFilter = false }) => {
  const { studentContext } = useWorkflow();

  // Use cached students from DataContext (shared with SessionPlanner)
  const { students, studentsLoading, getStudents } = useData();

  // Filter state
  const [studentId, setStudentId] = useState(initialStudentId || studentContext?.uuid || '');
  const [dateRange, setDateRange] = useState('all');

  // Ensure students are loaded (will use cache if available)
  // Only fetch if user has permission (admin/team_captain) and student filter is shown
  useEffect(() => {
    if (hideStudentFilter) return; // Don't fetch if student filter is hidden
    const userRole = getUserRole();
    if (userRole === 'admin' || userRole === 'team_captain') {
      getStudents().catch(() => {}); // Silently handle errors
    }
  }, [getStudents, hideStudentFilter]);

  // Initialize from workflow context if available
  useEffect(() => {
    if (initialStudentId) {
      setStudentId(initialStudentId);
    } else if (studentContext?.uuid && !studentId) {
      setStudentId(studentContext.uuid);
    }
  }, [initialStudentId, studentContext, studentId]);

  // Notify parent when filters change
  useEffect(() => {
    const filters = {};
    if (studentId) filters.student_id = studentId;
    if (dateRange !== 'all') filters.date_range = dateRange;

    onFiltersChange(filters);
  }, [studentId, dateRange, onFiltersChange]);

  const handleClearFilters = () => {
    setStudentId('');
    setDateRange('all');
  };

  // Don't count student filter as active if it's hidden (managed externally)
  const hasActiveFilters = (!hideStudentFilter && studentId) || dateRange !== 'all';

  return (
    <div className="session-filters">
      <div className="session-filters__controls">
        {/* Student Filter - conditionally hidden when managed by parent */}
        {!hideStudentFilter && (
          <div className="session-filters__field">
            <label htmlFor="student-filter">Student</label>
            <select
              id="student-filter"
              value={studentId}
              onChange={(e) => setStudentId(e.target.value)}
              disabled={studentsLoading}
            >
              <option value="">All Students</option>
              {students.map(student => (
                <option key={student.uuid} value={student.uuid}>
                  {getStudentDisplayName(student)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Date Range Filter */}
        <div className="session-filters__field">
          <label htmlFor="date-filter">Date Range</label>
          <select
            id="date-filter"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="all">All Time</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
        </div>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            className="session-filters__clear"
            onClick={handleClearFilters}
            title="Clear all filters"
          >
            Clear
          </button>
        )}
      </div>

      {/* Active Filter Indicator */}
      {hasActiveFilters && (
        <div className="session-filters__active">
          <span className="session-filters__active-label">Filters active:</span>
          {/* Only show student chip if student filter is visible */}
          {!hideStudentFilter && studentId && (() => {
            const student = students.find(s => s.uuid === studentId);
            const name = student
              ? getStudentDisplayName(student)
              : `(No name) ${studentId.substring(0, 8)}`;
            return (
              <span className="session-filters__chip">
                Student: {name}
              </span>
            );
          })()}
          {dateRange !== 'all' && (
            <span className="session-filters__chip">
              {dateRange === '7d' ? 'Last 7 days' :
               dateRange === '30d' ? 'Last 30 days' :
               'Last 90 days'}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default SessionFilters;
